package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Simulation
import com.tedd.db.Simulation_line
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Les simulations : des brouillons chiffrés, nommés, et leurs lignes. Rien ici ne
 * calcule : une ligne garde son expression telle que tapée, l'interface l'évalue.
 */
class SimulationRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Simulation>> =
        db.simulationQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeAllLines(): Flow<List<Simulation_line>> =
        db.simulationLineQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun linesOf(simulationId: String): List<Simulation_line> =
        db.simulationLineQueries.selectBySimulation(simulationId).executeAsList()

    suspend fun create(name: String, note: String? = null, id: String = newId("sim")): String =
        withContext(Dispatchers.Default) {
            val now = nowMillis()
            val rank = (db.simulationQueries.selectAll().executeAsList().maxOfOrNull { it.order_index } ?: -1L) + 1L
            db.simulationQueries.insert(id, name, note, rank, now, now)
            id
        }

    suspend fun rename(id: String, name: String, note: String?) = withContext(Dispatchers.Default) {
        db.simulationQueries.update(name, note, nowMillis(), id)
    }

    /** Copie un brouillon et ses lignes, sous un autre nom. */
    suspend fun duplicate(sourceId: String, newName: String): String? = withContext(Dispatchers.Default) {
        val source = db.simulationQueries.selectById(sourceId).executeAsOneOrNull() ?: return@withContext null
        val id = create(newName, source.note)
        val now = nowMillis()
        db.transaction {
            linesOf(sourceId).forEachIndexed { index, line ->
                db.simulationLineQueries.insert(newId("sl"), id, line.label, line.expression, index.toLong(), now, now)
            }
        }
        id
    }

    /** Supprimer un brouillon emporte ses lignes, sans corbeille. */
    suspend fun delete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            db.simulationLineQueries.softDeleteForSimulation(now, now, id)
            db.simulationQueries.softDelete(now, now, id)
        }
    }

    suspend fun addLine(simulationId: String, label: String, expression: String, id: String = newId("sl")): String =
        withContext(Dispatchers.Default) {
            val now = nowMillis()
            val rank = (linesOf(simulationId).maxOfOrNull { it.order_index } ?: -1L) + 1L
            db.simulationLineQueries.insert(id, simulationId, label, expression, rank, now, now)
            id
        }

    suspend fun updateLine(id: String, label: String, expression: String) = withContext(Dispatchers.Default) {
        db.simulationLineQueries.update(label, expression, nowMillis(), id)
    }

    suspend fun deleteLine(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.simulationLineQueries.softDelete(now, now, id)
    }

    /** Déplace une ligne d'un cran ; les rangs des lignes du brouillon sont renumérotés. */
    suspend fun moveLine(id: String, delta: Int) = withContext(Dispatchers.Default) {
        val line = db.simulationLineQueries.selectByIdAnyState(id).executeAsOneOrNull() ?: return@withContext
        val lines = linesOf(line.simulation_id).toMutableList()
        val from = lines.indexOfFirst { it.id == id }
        if (from < 0) return@withContext
        val to = (from + delta).coerceIn(0, lines.lastIndex)
        if (to == from) return@withContext
        lines.add(to, lines.removeAt(from))
        val now = nowMillis()
        db.transaction {
            lines.forEachIndexed { index, l ->
                if (l.order_index != index.toLong()) db.simulationLineQueries.setOrder(index.toLong(), now, l.id)
            }
        }
    }
}
